import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import {
  Box,
  Tabs,
  Tab,
  Button,
  Switch,
  IconButton,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomerList from "../../features/manageCustomers/customerList";
import CustomerTopbar from "../../features/manageCustomers/topbar";
import {
  useLazyDeleteEmployerQuery,
  useLazyGetEmployersQuery,
  useLazyUserExportQuery,
  useUpdateStatusCustomerMutation,
} from "../../services/users";
import { Employer } from "../../types/General";
import { ROLES } from "../../utils/enums";
import DownloadIcon from "@mui/icons-material/Download";
import { isValidInput } from "../../utils/validations";
import SearchBar from "../../components/SearchBar";
import TableComponent from "../../components/TableComponent";
import { GridColDef } from "@mui/x-data-grid";
import { showError, showToast } from "../../constants/toasts";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import usePermission from "../../hooks/usePermission";
import useTranslation from "../../hooks/traslation";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const ManageEmployer = () => {
  const navigate = useNavigate();
  const translate = useTranslation();
  const [employersList] = useLazyGetEmployersQuery();
  const [updateStatus] = useUpdateStatusCustomerMutation();
  const [deleteMethod] = useLazyDeleteEmployerQuery();
  const [exportCSV] = useLazyUserExportQuery();

  const [rows, setRows] = useState<Employer[]>([]);

  const [valueTabs, setValueTabs] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [count, setCount] = useState(0);
  const { isAdd, isDelete } = usePermission({
    label: "Manage Employers",
    page_permission: "isView",
  });

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");

  const label = { inputProps: { "aria-label": "Switch demo" } };
  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleStatusUpdate = async (id: string, status: boolean) => {
    const body = {
      isBlocked: status,
    };

    try {
      const response = await updateStatus({ id, body }).unwrap();
      if (response?.statusCode === 200) {
        const updatedData = rows?.map((item) => {
          if (item?._id === id) {
            return {
              ...item,
              isBlocked: status,
            };
          }
          return item;
        });
        setRows(updatedData);
        if (status === true) {
          showToast("Employer deactivated successfully");
        } else {
          showToast("Employer activated successfully");
        }
      } else {
        showError("Please try again");
      }
    } catch (error: any) {
      console.log(error, "errror");
      showError(error?.data?.message || "");
    }
  };

  const getExportCsv = async () => {
    try {
      const response = await exportCSV({
        page,
        limit: 10,
        search: debouncedSearchTerm.trim(),
        role: ROLES.EMPLOYER,
        status: valueTabs + 1,
      }).unwrap();
      if (response?.statusCode === 200) {
        window.open(response?.data || "");
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const getAllEmployers = async () => {
    setIsLoading(true);
    try {
      const response = await employersList({
        page,
        limit: 10,
        search: debouncedSearchTerm.trim(),
        role: ROLES.EMPLOYER,
      }).unwrap();
      if (response?.statusCode === 200) {
        setIsLoading(false);
        setRows(response?.data?.list || []);
        setCount(response?.data?.totalCount);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);

    try {
      const response = await deleteMethod({ id: selectedId || "" }).unwrap();
      setIsLoading(false);

      if (response?.statusCode === 200) {
        showToast("Employer deleted successfully");
        getAllEmployers();
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllEmployers();
  }, [debouncedSearchTerm, page, valueTabs]);

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 1,
      minWidth: 220,
      field: "name",
      headerName: translate.globals.name,
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div
            className="user_block click_action"
            onClick={() => navigate(`/manage-employers/detail/${row?._id}`)}
          >
            <figure>
              <img
                src={row?.image || "/static/images/user_placeholder.png"}
                alt=""
              />
            </figure>
            <p>
              <b>
                {row?.firstName || "-"} {row?.lastName || ""}
              </b>{" "}
              <span>{row?.email || ""}</span>
              <span>
                {row?.dialCode || "-"} {row?.phoneNo || "-"}
              </span>
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 200,
      field: "address",
      headerName: translate.main.Address,
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div>
            <p>{row?.address?.addressName || "-"}</p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 150,
      field: "totalTask",
      headerName: translate.main.totalTasks,
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <div>
            <p>{row?.totalTasks || "0"}</p>
          </div>
        );
      },
    },

    {
      flex: 1,
      minWidth: 100,
      field: "status",
      headerName: translate.globals.status,
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => (
        <Switch
          {...label}
          size="small"
          checked={!row?.isBlocked}
          onChange={() => handleStatusUpdate(row?._id, !row?.isBlocked)}
        />
      ),
    },
    {
      minWidth: 130,
      field: "action",
      headerName: translate.globals.actions,
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="table_actions v2">
            <Box>
              <Tooltip title="View">
                <IconButton
                  onClick={() =>
                    navigate(`/manage-employers/detail/${row?._id}`)
                  }
                >
                  <img src="/static/images/eye_icon.svg" alt="" />
                </IconButton>
              </Tooltip>
            </Box>
            {isAdd ? (
              <Box>
                <Tooltip title="Edit">
                  <IconButton
                    onClick={() =>
                      navigate(`/manage-employers/edit/${row?._id}`)
                    }
                  >
                    <img src="/static/images/edit2_icon.svg" alt="" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null}

            {isDelete ? (
              <Box>
                <Tooltip title="Delete">
                  <IconButton
                    onClick={() => {
                      setSelectedId(row?._id);
                      setOpenDelete(true);
                    }}
                  >
                    <img src="/static/images/trash2_icon.svg" alt="" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <MainContainer>
      <div className="main_title">
        <h1>{translate?.main?.ManageEmployer} </h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>
            {translate.dashboard.dashboard}
          </span>{" "}
          - {translate?.main?.ManageEmployer}
        </p>
      </div>

      {/* <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="Approved Customers" {...a11yProps(0)} />
              <Tab label="Pending Customers" {...a11yProps(1)} />
              <Tab label="Rejected Customers" {...a11yProps(2)} />
            </Tabs>
          </Box>
        </div>
      </div> */}

      <div className="cards">
        {/* <CustomerTopbar /> */}

        <div className="table_header">
          <div className="left_s">
            <Box className="search_box">
              <SearchBar
                placeholder={translate.user.SearchHere}
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
            </Box>
          </div>
          <div className="right_s">
            {/* {isAdd ? (
              <Button
                className="btn btn_primary"
                onClick={() => navigate("/manage-customers/add")}
              >
                <AddIcon /> Add
              </Button>
            ) : null} */}
            {rows.length !== 0 && (
              <Button className="btn btn_grey" onClick={getExportCsv}>
                <DownloadIcon /> {translate.main.export}
              </Button>
            )}
          </div>
        </div>

        <div className="sorting_table">
          <TableComponent
            columns={columns}
            rows={rows}
            count={count}
            page={page}
            onPageChange={handlePageChange}
            loading={isLoading}
          />
          {rows.length !== 0 && (
            <div className="table_footer">
              <div className="pagination_info">
                <span className="total_records">Showing {rows.length} of {count} records</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmationModal
        text={translate.error.warnEmployee}
        openModal={openDelete}
        setOpenModal={setOpenDelete}
        handleSubmit={handleDelete}
      />
    </MainContainer>
  );
};

export default ManageEmployer;
